import { cn } from '@/lib/utils';
import { TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ReactNode } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';

interface ResponsiveTabsListProps {
  children: ReactNode;
  className?: string;
  enableScrolling?: boolean;
}

export const ResponsiveTabsList = ({ 
  children, 
  className,
  enableScrolling = false 
}: ResponsiveTabsListProps) => {
  const isMobile = useIsMobile();
  
  // Always use scrollable layout on mobile, or when explicitly enabled
  if (isMobile || enableScrolling) {
    return (
      <div className="relative border-b border-border">
        <div className="overflow-x-auto scrollbar-hide scroll-smooth-touch">
          <TabsList className={cn(
            "inline-flex items-center justify-start",
            "w-max min-w-full",
            className
          )}>
            {children}
          </TabsList>
        </div>
        
        {/* Gradient overlays for scroll indication */}
        <div className="absolute left-0 top-0 h-full w-6 bg-gradient-to-r from-background via-background/80 to-transparent pointer-events-none opacity-60 transition-opacity duration-300" />
        <div className="absolute right-0 top-0 h-full w-6 bg-gradient-to-l from-background via-background/80 to-transparent pointer-events-none opacity-60 transition-opacity duration-300" />
        
        {/* Mobile scroll hint */}
        {isMobile && (
          <div className="text-xs text-muted-foreground text-center mt-2 opacity-70">
            Swipe to see more tabs
          </div>
        )}
      </div>
    );
  }

  // Grid layout for desktop with fewer tabs
  return (
    <div className="border-b border-border">
      <TabsList className={cn("grid w-full", className)}>
        {children}
      </TabsList>
    </div>
  );
};

interface ResponsiveTabsTriggerProps {
  value: string;
  icon?: ReactNode;
  label: string;
  mobileLabel?: string;
  className?: string;
}

export const ResponsiveTabsTrigger = ({ 
  value, 
  icon, 
  label, 
  mobileLabel,
  className 
}: ResponsiveTabsTriggerProps) => {
  const isMobile = useIsMobile();
  
  return (
    <TabsTrigger 
      value={value} 
      className={cn(
        "flex items-center justify-center gap-1 sm:gap-2",
        "text-xs sm:text-sm font-medium",
        "touch-target px-2 sm:px-4",
        "whitespace-nowrap",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        // Ensure minimum width on mobile
        "min-w-0 flex-shrink-0",
        className
      )}
    >
      {icon && (
        <span className="flex-shrink-0 w-4 h-4">
          {icon}
        </span>
      )}
      
      {/* Show mobile label if provided, otherwise show regular label */}
      <span className="truncate">
        {isMobile && mobileLabel ? mobileLabel : label}
      </span>
    </TabsTrigger>
  );
};